/**
 * `<RouteOutlet>` — renders the active surface from the `ShellMount` route registry
 * (frontend.md §2.1).
 *
 * The shell's six core surfaces AND Agent 9's `/editor` + `/chat` are all registered into
 * the `ShellMount` (not hardcoded in a router config) — so this outlet builds its
 * `<Routes>` from `mount.getRoutes()`. When Agent 9 registers `/editor` and `/chat`, they
 * appear here with no change to the shell (the contract working as designed).
 *
 * Each registered surface is wrapped in its own `<ErrorBoundary>` (frontend.md §4.2 — "one
 * per surface, plus an app-root fallback"): a render-phase crash in one surface is
 * contained; the shell, nav, and other surfaces stay alive.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components';
import { ROUTES } from '@/routes';
import { useShellMountSnapshot } from './ShellMountContext';

// NOTE: /sign-in and /sign-up are NOT mounted here — they're hoisted to AppRoot
// so they render as siblings of <AppShell> (full-bleed, no shell chrome). See
// frontend/src/AppRoot.tsx for the top-level Routes branch.

export function RouteOutlet(): JSX.Element {
  const mount = useShellMountSnapshot();
  const routes = mount.getRoutes();

  return (
    <main className="route-outlet">
      <Routes>
        {/* Default → Editor (the v1 home — Joplin-shape note authoring is the primary
            surface). The bare `/editor` resolves to the most recently updated note in
            `<EditorSurface>` itself (no notes ⇒ a scratch buffer). */}
        <Route path="/" element={<Navigate to={ROUTES.editor} replace />} />
        {routes.map((reg) => (
          <Route
            key={reg.pattern}
            path={reg.pattern}
            element={
              <ErrorBoundary boundaryName={reg.pattern}>{reg.element}</ErrorBoundary>
            }
          />
        ))}
        {/* Unknown route → Editor, not a blank screen (frontend.md §4.2 degraded-but-alive). */}
        <Route path="*" element={<Navigate to={ROUTES.editor} replace />} />
      </Routes>
    </main>
  );
}
